import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, Controller } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { getRedirectUrl } from "@/lib/utils/route"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ROUTES } from "@/routes/path"
import { logout } from "@/lib/http/auth"
import { useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useMutation } from "@tanstack/react-query"
import { createOrganization } from "@/lib/http/organization"
import { ApiError, CreateOrganizationInput } from "@/types/api"
import { ErrorCard } from "@/components/ui/error"
import { useListOrganizations, useOnSetOrganization, useSwitchOrganization } from "@/hooks/useOrganization"
import { useToast } from "@/hooks/useToast"
import { useAuth } from "@/hooks/useAuth"
import { useOrganizations } from "@/hooks/useOrganization"
import industries from "@/lib/data/industries.json"

const publicEmailDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com", "icloud.com"]

const createOrganizationSchema = z.object({
  organizationName: z.string().min(2, "Organization name is required"),
  industry: z.string({ required_error: "Please select an industry." }).min(1, "Please select an industry."),
  team: z.string({ required_error: "Please select your team." }).min(1, "Please select your team."),
  source: z.string().optional(),
})

type CreateOrganizationFormValues = z.infer<typeof createOrganizationSchema>

export default function CreateOrganization() {
  const [open, setOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const { email } = useAuth()
  const organizations = useOrganizations()

  const { refetch: refetchOrganizations } = useListOrganizations()
  const { mutate: switchOrganization } = useSwitchOrganization()
  const onSetOrganization = useOnSetOrganization()

  const getInitialOrgName = () => {
    if (!email) return ""

    try {
      const domain = email.split("@")[1]
      if (publicEmailDomains.includes(domain.toLowerCase())) {
        return ""
      }
      const orgName = domain.split(".")[0]
      return orgName.charAt(0).toUpperCase() + orgName.slice(1)
    } catch {
      return ""
    }
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<CreateOrganizationFormValues>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      organizationName: getInitialOrgName(),
      industry: "",
      team: "",
      source: "",
    },
  })

  const mutation = useMutation({
    mutationFn: (data: CreateOrganizationInput) => {
      setIsLoading(true)
      setFormError(null)
      return createOrganization(data)
    },
    onSuccess: async (organization) => {
      await Promise.all([refetchOrganizations(), switchOrganization(organization.id)]).then(([{ data }, _]) => {
        onSetOrganization(data!, organization)

        window.location.href = getRedirectUrl(searchParams, ROUTES.DASHBOARD.STUDIO.INDEX)

        toast({
          title: "Organization created",
          description: "Your organization has been created successfully.",
          variant: "success",
        })
      })
    },
    onError: (error: ApiError) => {
      setIsLoading(false)
      setFormError(error?.response?.data?.error || "An unknown error occurred.")
    },
  })

  const selectedIndustry = watch("industry")

  async function onSubmit(data: CreateOrganizationFormValues) {
    const payload: CreateOrganizationInput = {
      name: data.organizationName,
      industry: data.industry,
      team: data.team,
      source: data.source || "",
    }
    mutation.mutate(payload)
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-zinc-900 lg:flex-row">
      {/* Left column: features */}
      <div className="z-0 hidden w-1/2 flex-col bg-gradient-to-tr from-amber-300 via-lime-200 to-green-300 px-16 lg:flex">
        <div className="mt-10">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>
        <ul className="mt-24 space-y-8 text-black">
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Get started quickly</h5>
            <p className="mt-1 pl-4">Create or join an organization to start collaborating.</p>
          </li>
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Support any business model</h5>
            <p className="mt-1 pl-4">
              Ecommerce, fintech, SaaS platforms, marketplaces, and more—all within a unified platform.
            </p>
          </li>
          <li>
            <h5 className="border-l-2 border-black pl-4 text-lg font-semibold">Join millions of businesses</h5>
            <p className="mt-1 pl-4">Ledgerbeam is trusted by ambitious startups and enterprises of every size.</p>
          </li>
        </ul>
      </div>

      <div className="mt-6 flex justify-center lg:hidden">
        <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
      </div>

      {/* Right column: selection card */}
      <div className="relative mt-2 flex flex-1 justify-center p-4 md:items-center lg:mt-5 lg:py-8">
        <div className="relative z-10 w-full max-w-lg">
          <div className="rounded-xl bg-white px-8 py-4 shadow-xl dark:bg-zinc-800">
            <h1 className="mb-2 text-2xl font-semibold dark:text-zinc-100">Create your organization</h1>
            <p className="mb-6 text-zinc-600 dark:text-zinc-400">
              We need a few more details before you can get started.
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-5">
                <div>
                  <Input
                    label="Organization name"
                    {...register("organizationName")}
                    error={errors.organizationName?.message}
                    touched={touchedFields.organizationName}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm font-medium">What industry is your organization in?</Label>
                  <Controller
                    name="industry"
                    control={control}
                    render={({ field }) => (
                      <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className={cn("w-full justify-between", errors.industry && "border-red-500")}
                          >
                            {selectedIndustry ? (
                              industries.find((industry) => industry.value === selectedIndustry)?.label
                            ) : (
                              <span className="text-muted-foreground">Select an industry...</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                          <Command>
                            <CommandInput placeholder="Search industries..." />
                            <CommandList>
                              <CommandEmpty>No industry found.</CommandEmpty>
                              <CommandGroup>
                                {industries.map((industry) => (
                                  <CommandItem
                                    key={industry.value}
                                    value={industry.label}
                                    onSelect={(currentValue) => {
                                      const selected = industries.find(
                                        (i) => i.label.toLowerCase() === currentValue.toLowerCase()
                                      )
                                      if (selected) {
                                        field.onChange(selected.value === field.value ? "" : selected.value)
                                      }
                                      setOpen(false)
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === industry.value ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {industry.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.industry && <p className="mt-1 text-sm text-red-500">{errors.industry.message}</p>}
                </div>

                <div>
                  <Label className="mb-1 block text-sm font-medium">Your team</Label>
                  <Controller
                    name="team"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className={cn(errors.team && "border-red-500")}>
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="engineering">Engineering</SelectItem>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="customer-support">Customer Support</SelectItem>
                          <SelectItem value="design">Design</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.team && <p className="mt-1 text-sm text-red-500">{errors.team.message}</p>}
                </div>

                {!organizations ||
                  (organizations.length === 0 && (
                    <div>
                      <Label className="mb-1 block text-sm font-medium">Where did you hear about us?</Label>
                      <Controller
                        name="source"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger className={cn(errors.source && "border-red-500")}>
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                              <SelectItem value="Twitter">Twitter</SelectItem>
                              <SelectItem value="Google/Search Engine">Google/Search Engine</SelectItem>
                              <SelectItem value="Referral">Referral</SelectItem>
                              <SelectItem value="Event">Event</SelectItem>
                              <SelectItem value="Product Hunt">Product Hunt</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.source && <p className="mt-1 text-sm text-red-500">{errors.source.message}</p>}
                    </div>
                  ))}

                {formError && <ErrorCard message={formError} />}

                <div className="mt-6">
                  <Button type="submit" className="w-full" disabled={mutation.isPending || isLoading}>
                    {mutation.isPending || isLoading ? "Creating..." : `Create`}
                  </Button>
                </div>
              </div>
            </form>
          </div>
          <div className="-mt-4 w-full max-w-lg rounded-b-xl border-t bg-gray-50 px-8 py-5 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
            Already have a team in Ledgerbeam?{" "}
            <Link
              to={ROUTES.DASHBOARD.ORGANIZATION.JOIN}
              className="font-medium text-primary hover:underline dark:text-primary"
            >
              Join existing organization
            </Link>{" "}
            or{" "}
            <span
              onClick={() => logout().then(() => navigate(ROUTES.AUTH.LOGIN))}
              className="cursor-pointer font-medium text-primary hover:underline dark:text-primary"
            >
              use a different account
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
